import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  paymentPlanSaleInvoiceGetAll,
  paymentPlanSaleInvoiceExcel,
} from '../../../redux/slices/paymentplan.slice'
import {allCompanyLookup, projectLookup, basicLookup} from '../../../redux/slices/lookup.slice'

import {pagination} from '../../../helpers'

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()

  const selectSaleData = (state) => ({
    saleinvoice: state.paymentplan.saleinvoice,
    loading: state.paymentplan.loading
  })

  const selectLookupData = (state) => ({
    projectLookupList: state.lookup.projectLookupList,
    companyLookup: state.lookup.companyLookup,
    PaymentPlanType: state.lookup.PaymentPlanType
  })

  const {saleinvoice, loading} = useSelector(selectSaleData)
  const {projectLookupList, companyLookup, PaymentPlanType} = useSelector(selectLookupData)

  const [params, setParams] = useState(pagination)

  useEffect(() => {
    dispatch(paymentPlanSaleInvoiceGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(allCompanyLookup())
    dispatch(basicLookup('PaymentPlanType'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const handleExcelExport = useCallback(() => {
    if (params) {
      dispatch(
        paymentPlanSaleInvoiceExcel({
          params,
          fileName: intl.formatMessage({id: 'MENU.CASH.SALE'}),
        })
      )
    }
  }, [dispatch, params, intl])

  const Columns = useMemo(() => [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.PROJECTNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'projectName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.COMPANYNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'companyName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.TOTALPRICE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'totalPrice',
      Cell: ({...props}) => <CustomCell item={props} price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.SALEDEFERMENTDATE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'defermentDate',
      Cell: ({...props}) => <CustomCell item={props} date='short' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.PAYMENTPLANTYPENAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'paymentPlanTypeName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'PAYMENTPLAN.GET.DESCRIPTION'})}
          className='min-w-125px'
        />
      ),
      accessor: 'description',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ], [intl, handleSorting])

  const actionButtons = useMemo(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handleExcelExport,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'PAYMENTPLAN.GET.PROJECTNAME',
          data: projectLookupList,
          name: 'projectId',
        },
        {
          title: 'PAYMENTPLAN.GET.COMPANYNAME',
          data: companyLookup,
          name: 'companyId',
        },
        {
          title: 'PAYMENTPLAN.GET.PAYMENTPLANTYPENAME',
          data: PaymentPlanType,
          name: 'paymentPlanTypeId',
        },
        {
          title: 'PAYMENTPLAN.GET.STARTDATE',
          type: 'date',
          name: 'startDate',
        },
        {
          title: 'PAYMENTPLAN.GET.ENDDATE',
          type: 'date',
          name: 'endDate',
        },
      ],
    },
  ], [projectLookupList, companyLookup, PaymentPlanType, handleExcelExport])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'MENU.CASH.SALE'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
        />
        <KTCardBody>
          {!saleinvoice && <ListLoading />}
          {saleinvoice && (
            <Table
              items={saleinvoice}
              cols={Columns}
              handlePagination={handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
